import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

/**
 * A numbered procedure.
 *
 * The numbers come from a CSS counter rather than from React, for the same
 * reason the published page uses a real `<ol>`: nothing has to renumber when a
 * step is inserted, removed or dragged, so the editor and the published page
 * cannot disagree about what step 3 is.
 *
 * In Markdown this is `::::::steps` around `:::::step`, and the colon counts
 * shift depending on what each step contains. That is exactly the detail a
 * writer should never have to think about, which is why this block exists.
 */
export function StepsView({ node, editor, getPos }: NodeViewProps) {
  const addStep = () => {
    const pos = getPos?.()
    if (pos === undefined) return
    // Insert just inside the closing token, so the new step lands last.
    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize - 1, {
        type: 'step',
        attrs: { title: null },
        content: [{ type: 'paragraph' }],
      })
      .run()
  }

  return (
    <NodeViewWrapper className="be-steps" data-sls="steps">
      <NodeViewContent />
      {editor.isEditable && (
        <div className="be-steps__foot" contentEditable={false}>
          <button className="be-steps__add" onClick={addStep} type="button">
            + Add step
          </button>
        </div>
      )}
    </NodeViewWrapper>
  )
}

/**
 * One step. The title is optional — an untitled step is still numbered, so the
 * placeholder must not imply the field is required.
 */
export function StepView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
  const remove = () => {
    const from = getPos?.()
    if (from === undefined) return
    editor
      .chain()
      .focus()
      .deleteRange({ from, to: from + node.nodeSize })
      .run()
  }

  return (
    <NodeViewWrapper className="be-step" data-sls="step">
      <div className="be-step__head" contentEditable={false}>
        <input
          className="be-inline-input"
          value={(node.attrs['title'] as string | null) ?? ''}
          placeholder="Step title (optional)"
          disabled={!editor.isEditable}
          onChange={(e) => updateAttributes({ title: e.target.value || null })}
        />
        {editor.isEditable && (
          <button className="be-icon-btn" onClick={remove} type="button" data-tip="Remove step">
            ×
          </button>
        )}
      </div>
      <NodeViewContent className="be-step__body" />
    </NodeViewWrapper>
  )
}
